import { initials } from '@dicebear/collection';
import { createAvatar } from '@dicebear/core';
import { useMutation } from '@tanstack/react-query';
import { renameClassroom } from 'api/classroomApi';
import { queryClient } from 'App';
import classNames from 'classnames';
import Avatar from 'components/common/Avatar';
import CardDropdown from 'components/common/CardDropdown';
import CapoclasseCrown from 'components/fantaclasse/CapoclasseCrown';
import LoadingButton from 'components/fantaclasse/LoadingButton';
import Loading from 'pages/components/Loading';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, Dropdown, Form, Modal, Row, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import PrefectActionModal from './PrefectActionModal';
import RemoveUserModal from './RemoveUserModal';

export const ManageClassroomModalItem = ({
  user,
  isMe = false,
  showDropdown = true,
  setUserToRemove,
  setPrefectUser
}) => {
  const userProfilePic = createAvatar(initials, {
    seed: user.username,
    size: 100,
    backgroundColor: ['1e88e5']
  }).toDataUriSync();

  return (
    <tr>
      <td className="text-nowrap">
        <div className="d-flex align-items-center">
          <Avatar src={userProfilePic} size="xl" />
          {user.prefect && <CapoclasseCrown className="ms-2" />}
          <h5
            className={classNames('fs-9 mb-0', {
              'ms-2': user.prefect,
              'ms-3': !user.prefect
            })}
          >
            {user.username}
          </h5>
        </div>
      </td>
      {showDropdown && !isMe && (
        <td className="text-end">
          <CardDropdown>
            <div className="py-2">
              {user.prefect ? (
                <Dropdown.Item
                  className="text-warning"
                  onClick={() => setPrefectUser(user)}
                >
                  Rimuovi dai capoclasse
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  className="text-primary"
                  onClick={() => setPrefectUser(user)}
                >
                  Nomina capoclasse
                </Dropdown.Item>
              )}
              <Dropdown.Item
                className="text-danger"
                onClick={() => setUserToRemove(user.username)}
              >
                Rimuovi dalla classe
              </Dropdown.Item>
            </div>
          </CardDropdown>
        </td>
      )}
    </tr>
  );
};

ManageClassroomModalItem.propTypes = {
  user: PropTypes.object.isRequired,
  isMe: PropTypes.bool,
  showDropdown: PropTypes.bool,
  setUserToRemove: PropTypes.func,
  setPrefectUser: PropTypes.func
};

const ManageClassroomModal = ({
  showModal,
  setShowModal,
  classroom,
  username,
  users
}) => {
  const [classroomName, setClassroomName] = useState(classroom.name);

  const [isRenaming, setIsRenaming] = useState(false);
  const renameClassroomMutation = useMutation({
    mutationFn: renameClassroom,
    onMutate: () => {
      setIsRenaming(true);
    },
    onSuccess: data => {
      setIsRenaming(false);
      queryClient.invalidateQueries({ queryKey: ['classroomData'] });
      toast.success(() => (
        <span>
          Il nome della classe è stato modificato in{' '}
          <strong>{data.name}</strong>
        </span>
      ));
    },
    onError: err => {
      setIsRenaming(false);
      toast.error(err.response?.data?.message || err.message);
    }
  });

  const handleRenameClassroom = async () => {
    renameClassroomMutation.mutate(classroomName);
  };

  const [userToRemove, setUserToRemove] = useState(null);
  const [prefectUser, setPrefectUser] = useState(null);

  // Close modal when userToRemove or userToNominate is set (RemoveUserModal or NominatePrefectModal is open)
  useEffect(() => {
    if (showModal && (userToRemove !== null || prefectUser !== null)) {
      setShowModal(false);
    }
  }, [showModal, userToRemove, prefectUser]);

  return (
    <>
      <RemoveUserModal
        userToRemove={userToRemove}
        setUserToRemove={setUserToRemove}
        setShowModal={setShowModal}
      />
      <PrefectActionModal
        prefectUser={prefectUser}
        setPrefectUser={setPrefectUser}
        setShowModal={setShowModal}
      />
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Gestisci classe
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {!showModal ? (
            <Loading height="200px" />
          ) : (
            <>
              <h5 className="fs-9 py-2">Nome</h5>
              <Row className="g-2 mb-2">
                <Col sm>
                  <Form.Control
                    placeholder="Nome della classe"
                    value={classroomName}
                    onChange={e => setClassroomName(e.target.value)}
                  />
                </Col>
                <Col sm="auto">
                  <LoadingButton
                    text="Modifica"
                    isLoading={isRenaming}
                    disabled={classroomName === classroom.name}
                    action={handleRenameClassroom}
                  />
                </Col>
              </Row>
              <h5 className="fs-9 py-2">Partecipanti</h5>
              <Table striped responsive>
                <tbody>
                  {users.map((user, index) => (
                    <ManageClassroomModalItem
                      user={user}
                      key={index}
                      isMe={username == user.username}
                      setUserToRemove={setUserToRemove}
                      setPrefectUser={setPrefectUser}
                    />
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

ManageClassroomModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  classroom: PropTypes.object.isRequired,
  username: PropTypes.string.isRequired,
  users: PropTypes.array.isRequired
};

export default ManageClassroomModal;
